import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useI18n } from "../lib/i18n";
import { useCurrency, CURRENCIES } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { Menu, X, Globe, ChevronDown, LogOut, Search } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

const LANGS = [{ code: "en", label: "English" }, { code: "ar", label: "العربية" }, { code: "fr", label: "Français" }];

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const { currency, setCurrency } = useCurrency();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/marketplace", label: "Browse" },
    { to: "/valuation", label: "Free Valuation" },
    { to: "/idea-evaluator", label: "Idea Evaluator" },
    { to: "/matches", label: "Matches" },
    { to: "/founder", label: "Our Story" },
  ];

  return (
    <header className="dv-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-[64px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-lg">D</div>
          <span className="font-bold text-xl tracking-tight">DealVest</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) => `px-4 py-2 rounded-lg text-[15px] font-medium transition-colors ${isActive ? "text-blue-600 bg-blue-50" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-switcher" className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg">
                <Globe size={15} /> {lang.toUpperCase()} <ChevronDown size={12} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {LANGS.map(l => <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}>{l.label}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="currency-switcher" className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg">
                {currency} <ChevronDown size={12} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-72 overflow-y-auto">
              {Object.keys(CURRENCIES).map(c => <DropdownMenuItem key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}><span className="font-semibold mr-2">{c}</span><span className="text-slate-500 text-xs">{CURRENCIES[c].name}</span></DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <Link to="/admin" className="dv-btn dv-btn-secondary dv-btn-sm" data-testid="header-admin-btn">Admin</Link>
              )}
              <Link to="/dashboard" className="dv-btn dv-btn-ghost dv-btn-sm" data-testid="header-dashboard-btn">{user.full_name || user.email.split("@")[0]}</Link>
              <button onClick={() => { logout(); navigate("/"); }} className="dv-btn dv-btn-ghost dv-btn-sm" data-testid="header-logout-btn"><LogOut size={14} /></button>
            </div>
          ) : (
            <>
              <Link to="/login" className="dv-btn dv-btn-ghost dv-btn-sm" data-testid="header-login-btn">Log in</Link>
              <Link to="/signup" className="dv-btn dv-btn-primary dv-btn-sm" data-testid="header-signup-btn">Sign up</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-slate-700 p-2 -mr-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map(l => <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-3 px-3 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-50">{l.label}</Link>)}
            <div className="border-t border-slate-100 mt-2 pt-3 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="dv-btn dv-btn-ghost w-full">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="dv-btn dv-btn-ghost w-full">Log out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="dv-btn dv-btn-ghost w-full">Log in</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="dv-btn dv-btn-primary w-full">Sign up</Link>
                </>
              )}
            </div>
            <div className="flex gap-2 pt-3 mt-2 border-t border-slate-100">
              {LANGS.map(l => <button key={l.code} onClick={() => setLang(l.code)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${lang === l.code ? "bg-blue-50 text-blue-600" : "text-slate-500"}`}>{l.label}</button>)}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
